import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, TrendingUp, TrendingDown, Package, Award } from "lucide-react";
import { toast } from "sonner";
import InteractiveTour from "@/components/InteractiveTour";

export default function Uebersicht() {
  const [activeTab, setActiveTab] = useState("geschäftlich");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tourReady, setTourReady] = useState(false);

  // Tour sofort aktivieren falls localStorage gesetzt ist
  useEffect(() => {
    // Tour ist bereit
    setTourReady(true);
  }, []);

  // User laden für InteractiveTour
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    loadOverview();
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const loadOverview = async () => {
    try {
      const typeParam = activeTab === "gesamt" ? "" : `?type=${encodeURIComponent(activeTab)}`;
      const response = await axios.get(`${API}/overview${typeParam}`);
      setOverview(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Übersicht");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-slate-600">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link 
          to="/dashboard" 
          data-tour="zurueck-dashboard-button"
          onClick={() => {
            // Wenn Tour aktiv, setze localStorage für Tour-Fortsetzung auf Geschenk-Seite
            const existingTour = localStorage.getItem('burningTeamTour');
            if (existingTour || window.burningTeamTourActive) {
              console.log('🔙 Zurück zu Dashboard für Geschenk - setze localStorage');
              localStorage.setItem('burningTeamTour', JSON.stringify({
                shouldStart: true,
                page: 'geschenk',
                globalStep: 13
              }));
            }
          }}
        >
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border border-slate-200 mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 mb-6 flex items-center">
            <Package className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-teal-600 flex-shrink-0" />
            <span className="truncate">Bestandsübersicht</span>
          </h1>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
            <button
              onClick={() => handleTabChange("geschäftlich")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "geschäftlich"
                  ? "text-teal-600 border-b-2 border-teal-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Geschäftlich
            </button>
            <button
              onClick={() => handleTabChange("haushalt")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "haushalt"
                  ? "text-teal-600 border-b-2 border-teal-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Haushalt
            </button>
            <button
              onClick={() => handleTabChange("gesamt")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "gesamt"
                  ? "text-teal-600 border-b-2 border-teal-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Gesamt
            </button>
          </div>
          <p className="text-slate-500 mb-8">Aktueller Bestand mit Durchschnittspreisen</p>

          {/* Bestandsübersicht - ZUERST */}
          <div className="overflow-x-auto" data-tour="bestand-tabelle">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-2 font-semibold text-slate-700">Artikel</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700 bg-emerald-50">
                    <div className="flex items-center justify-end">
                      <Package className="w-4 h-4 mr-1 text-emerald-600" />
                      Bestand
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">Ø EK-Preis</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">
                    <div className="flex items-center justify-end">
                      <TrendingUp className="w-4 h-4 mr-1 text-green-600" />
                      Zugänge
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">
                    <div className="flex items-center justify-end">
                      <TrendingDown className="w-4 h-4 mr-1 text-red-600" />
                      Verkäufe
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">Geschenke</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">Werbung</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700" data-tour="demo-spalte">Demos</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">Eigenverbr.</th>
                  <th className="text-center py-3 px-2 font-semibold text-slate-700" data-tour="demo-prozent-spalte">Demo %</th>
                </tr>
              </thead>
              <tbody>
                {overview?.stock_data?.map((item, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-2 font-medium">{item.article}</td>
                    <td className={`py-3 px-2 text-right font-bold bg-emerald-50 ${
                      item.stock > 0 ? 'text-emerald-600' : 
                      item.stock < 0 ? 'text-red-600' : 
                      'text-slate-500'
                    }`}>
                      {item.stock}
                    </td>
                    <td className="py-3 px-2 text-right text-blue-600 font-semibold">
                      {item.avg_ek_price > 0 ? `${item.avg_ek_price.toFixed(2)} €` : '-'}
                    </td>
                    <td className="py-3 px-2 text-right text-green-600">{item.incoming}</td>
                    <td className="py-3 px-2 text-right text-red-600">{item.sales}</td>
                    <td className="py-3 px-2 text-right">{Math.round(item.gifts)}</td>
                    <td className="py-3 px-2 text-right text-cyan-600">{item.werbeartikel || 0}</td>
                    <td className="py-3 px-2 text-right">{item.demos}</td>
                    <td className="py-3 px-2 text-right">{item.personal_use}</td>
                    <td className="py-3 px-2 text-center">
                      {item.demo_percent_remaining !== null ? (
                        <span className="inline-block px-2 py-1 rounded bg-indigo-100 text-indigo-700 text-xs font-semibold">
                          {item.demo_percent_remaining}%
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top 3 Verkaufsartikel - DANACH */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center">
            <Award className="w-6 h-6 mr-2 text-amber-500" />
            Top 3 Verkaufsartikel
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {overview?.top_3_sales?.map((item, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-6"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="text-3xl font-bold text-emerald-600">#{index + 1}</div>
                  <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold">
                    {item.sales} Verkäufe
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-800">{item.article}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Interactive Tour - immer rendern für localStorage-Check */}
      {tourReady && (
        <InteractiveTour 
          user={user}
          onComplete={() => {
            // Tour completed
          }}
        />
      )}
    </div>
  );
}